import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword,
  UserCredential,
} from 'firebase/auth'
import { doc, DocumentData, DocumentSnapshot, getDoc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import moment from 'moment'
import { channel } from 'redux-saga'
import { ChannelTakeEffect, fork, put, take, takeEvery } from 'redux-saga/effects'
import User, { UserType } from 'redux/models/user'
import * as Auth from 'redux/modules/auth'
import { APP_URL } from 'utils/constant'
import { convertAuthError } from 'utils/firebase'
import { auth, firestore, functions } from 'utils/firebaseApp'

type LogInAction = ReturnType<typeof Auth.actions.logIn>
type SignUpAction = ReturnType<typeof Auth.actions.signUp>
type ChangeEmailAction = ReturnType<typeof Auth.actions.changeEmail>
type ChangePasswordAction = ReturnType<typeof Auth.actions.changePassword>
type ResetPasswordAction = ReturnType<typeof Auth.actions.resetPassword>
type DeleteAccountAction = ReturnType<typeof Auth.actions.deleteAccount>

const redirectChannel = channel()

let userInfoUnsubscribe: (() => void) | null = null

const loadAuth = function* () {
  yield take(Auth.LOAD)

  // auth 情報読み込み
  onAuthStateChanged(auth, async (firebaseUser) => {
    userInfoUnsubscribe && userInfoUnsubscribe()

    if (!firebaseUser) {
      redirectChannel.put(Auth.actions.syncAuth(null))
      return
    }

    userInfoUnsubscribe = onSnapshot(doc(firestore, 'users', firebaseUser.uid), (snapshot) => {
      const user = User.loadAuth(firebaseUser, snapshot.data())
      if (!user || user.isDeleted) {
        redirectChannel.put(Auth.actions.syncAuth(null))
      } else {
        redirectChannel.put(Auth.actions.syncAuth(user))
      }
    })
  })
}

const logIn = function* (action: LogInAction) {
  try {
    const { email, password } = action.payload

    const credential: UserCredential = yield signInWithEmailAndPassword(auth, email, password)
    const firebaseUser = credential.user
    if (!firebaseUser) throw new Error()

    const userRef: DocumentSnapshot<DocumentData> = yield getDoc(doc(firestore, 'users', firebaseUser.uid))

    const user = User.loadAuth(firebaseUser, userRef.data())

    if (!user) throw new Error()

    yield put(Auth.actions.succeededToLogIn(user))
  } catch (e: any) {
    const message = (e.code && convertAuthError(e.code, 'login')) || 'ログインできませんでした。'
    yield put(Auth.actions.failedToLogIn(message))
  }
}

const logOut = function* () {
  try {
    yield signOut(auth)
    yield put(Auth.actions.succeededToLogOut())
  } catch {
    const message = 'なんらかの理由でログアウトできませんでした。'
    yield put(Auth.actions.failedToLogOut(message))
  }
}

const signUp = function* (action: SignUpAction) {
  try {
    const { name, email, password } = action.payload

    // ユーザ生成
    const credential: UserCredential = yield createUserWithEmailAndPassword(auth, email, password)
    const firebaseUser = credential.user
    if (!firebaseUser) throw new Error()

    const date = moment()
    const user = new User(
      firebaseUser.uid,
      name,
      email,
      UserType.User,
      firebaseUser.emailVerified,
      null,
      0,
      0,
      false,
      date,
      date,
    )

    // メアド確認用のメールを送信
    yield sendEmailVerification(credential.user, { url: APP_URL })

    // ユーザ情報を firestore に登録
    yield setDoc(doc(firestore, 'users', user.id), user.documentData)

    // メール送信
    try {
      yield httpsCallable(functions, 'sendAccountCreatedMail')()
    } catch (e) {
      console.log(e)
    }

    yield put(Auth.actions.succeededToSignUp(user))
  } catch (e: any) {
    const message = (e.code && convertAuthError(e.code, 'signup')) || 'アカウントを作成できませんでした。'
    yield put(Auth.actions.failedToSignUp(message))
  }
}

const sendVerificationEmail = function* () {
  const user = auth.currentUser
  if (user && !user.emailVerified) {
    yield sendEmailVerification(user, { url: APP_URL })
  }

  // 連続送信防止
  setTimeout(() => redirectChannel.put(Auth.actions.sentVerificationEmail()), 5000)
}

const changeEmail = function* (action: ChangeEmailAction) {
  const firebaseUser = auth.currentUser
  if (!firebaseUser || !firebaseUser.email) return

  const { currentPassword, newEmail } = action.payload

  const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword)

  try {
    // 再認証
    yield reauthenticateWithCredential(firebaseUser, credential)

    // Email 更新
    yield updateEmail(firebaseUser, newEmail)

    // 確認メール送信
    yield sendEmailVerification(firebaseUser, { url: APP_URL })

    // ユーザ情報を更新
    const userDoc: DocumentSnapshot<DocumentData> = yield getDoc(doc(firestore, 'users', firebaseUser.uid))

    yield updateDoc(userDoc.ref, {
      email: newEmail,
      updatedAt: moment().unix(),
    })

    const user = User.loadAuth(firebaseUser, userDoc.data())
    yield put(Auth.actions.syncAuth(user))

    // 完了アクション
    yield put(Auth.actions.succeededToChangeEmail())
  } catch (e: any) {
    const message = (e.code && convertAuthError(e.code, 'changeEmail')) || 'メールアドレスを更新できませんでした。'
    yield put(Auth.actions.failedToChangeEmail(message))
  }
}

const changePssword = function* (action: ChangePasswordAction) {
  const firebaseUser = auth.currentUser
  if (!firebaseUser || !firebaseUser.email) return

  const { currentPassword, newPassword } = action.payload

  const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword)

  try {
    // 再認証
    yield reauthenticateWithCredential(firebaseUser, credential)

    // Email 更新
    yield updatePassword(firebaseUser, newPassword)

    // ユーザ情報を更新
    const userDoc: DocumentSnapshot<DocumentData> = yield getDoc(doc(firestore, 'users', firebaseUser.uid))

    yield updateDoc(userDoc.ref, { updatedAt: moment().unix() })

    const user = User.loadAuth(firebaseUser, userDoc.data())
    yield put(Auth.actions.syncAuth(user))

    // 完了アクション
    yield put(Auth.actions.succeededToChangePassword())
  } catch (e: any) {
    const message = (e.code && convertAuthError(e.code, 'changePassword')) || 'パスワードを更新できませんでした。'
    yield put(Auth.actions.failedToChangePassword(message))
  }
}

const resetPassword = function* (action: ResetPasswordAction) {
  yield sendPasswordResetEmail(auth, action.payload, { url: APP_URL })

  // 連続送信防止
  setTimeout(() => redirectChannel.put(Auth.actions.completedToResetPassword()), 5000)
}

const deleteAccount = function* (action: DeleteAccountAction) {
  const password = action.payload
  const user = auth.currentUser
  if (!user || !user.email) return

  try {
    const credential = EmailAuthProvider.credential(user.email, password)

    // 再認証
    yield reauthenticateWithCredential(user, credential)

    // 削除
    yield httpsCallable(functions, 'deleteAccount')({ userIds: [user.uid] })

    yield put(Auth.actions.completedToDeleteAccount(null))
  } catch (e: any) {
    const message = (e.code && convertAuthError(e.code, 'deleteAccount')) || 'アカウントを削除できませんでした。'
    console.error(message)
    yield put(Auth.actions.completedToDeleteAccount(message))
  }
}

export default function* dataSaga() {
  yield fork(loadAuth)

  yield takeEvery(Auth.LOGIN, logIn)
  yield takeEvery(Auth.LOGOUT, logOut)
  yield takeEvery(Auth.SIGNUP, signUp)
  yield takeEvery(Auth.SEND_VARIFICATION_EMAIL, sendVerificationEmail)
  yield takeEvery(Auth.CHANGE_EMAIL, changeEmail)
  yield takeEvery(Auth.CHANGE_PASSWORD, changePssword)
  yield takeEvery(Auth.RESET_PASSWORD, resetPassword)
  yield takeEvery(Auth.DELETE_ACCOUNT, deleteAccount)

  while (true) {
    const action: ChannelTakeEffect<unknown> = yield take(redirectChannel)
    yield put(action)
  }
}
